import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import SummaryCards from "../../components/Common/SummaryCards";
import UnsavedChangesDialog from "../../components/Common/UnsavedChangesDialog";
import ActionMenu from "../../components/Common/ActionMenu";
import NewClientDrawer from "../../components/Clients/NewClientDrawer";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import {
  createUpdateClient,
  deleteClient,
  getClientDetail,
  getClientTypes,
  getIndustryTypes,
  getLoggedInUser,
  getNextClientCode,
  searchClients,
} from "../../helpers/backend_helper";
import DataImportWizardPage from "../DataImport/DataImportWizardPage";
import "./ClientList.scss";

const blankClient = {
  name: "",
  code: "",
  type: "",
  industry: "",
  contact: "",
  email: "",
  phone: "",
  risk: "",
  status: "Active",
  notes: "",
};

const FilterSelect = ({ label, name, options, value, onChange }) => (
  <label className="client-filter">
    <span>{label}</span>
    <select
      name={name}
      value={value}
      onChange={onChange}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {options.map((option) => (
        <option key={option}>{option}</option>
      ))}
    </select>
  </label>
);

const ClientActionMenu = ({
  client,
  onEdit,
  onViewDetails,
  onDelete,
  canManage,
}) => {
  return (
    <ActionMenu
      label={client.name}
      triggerClassName="client-action"
      actions={[
        ...(canManage
          ? [
              {
                label: "Edit client",
                icon: <i className="bx bx-edit-alt" />,
                onSelect: () => onEdit(client),
              },
            ]
          : []),
        {
          label: "View Details",
          icon: <i className="bx bx-show-alt" />,
          onSelect: () => onViewDetails(client),
        },
        ...(canManage
          ? [
              {
                label: "Delete client",
                icon: <i className="bx bx-trash" />,
                className: "is-danger",
                separatorBefore: true,
                onSelect: () => onDelete(client),
              },
            ]
          : []),
      ]}
    />
  );
};

const getClientColumns = (onEdit, onViewDetails, onDelete, canManage) => [
  {
    id: "name",
    header: "Client Name",
    accessorKey: "name",
    size: 210,
    flex: 1.25,
    cell: ({ getValue }) => (
      <span className="client-grid-name">{getValue()}</span>
    ),
  },
  {
    id: "code",
    header: "Client Code",
    accessorKey: "code",
    size: 135,
    flex: 0.85,
  },
  {
    id: "type",
    header: "Client Type",
    accessorKey: "type",
    size: 155,
    flex: 1,
  },
  {
    id: "industry",
    header: "Industry",
    accessorKey: "industry",
    size: 180,
    flex: 1.1,
  },
  {
    id: "risk",
    header: "Risk Level",
    accessorKey: "risk",
    size: 125,
    flex: 0.8,
    cell: ({ getValue }) => (
      <span
        className={`client-pill client-pill--${(
          getValue() || "low"
        ).toLowerCase()}`}
      >
        {getValue() || "Low"}
      </span>
    ),
  },
  {
    id: "status",
    header: "Status",
    accessorKey: "status",
    size: 125,
    flex: 0.8,
    cell: ({ getValue }) => (
      <span
        className={`client-status client-status--${(
          getValue() || "inactive"
        ).toLowerCase()}`}
      >
        {getValue() || "Inactive"}
      </span>
    ),
  },
  {
    id: "engagements",
    header: "Engagements",
    accessorKey: "engagements",
    size: 120,
    flex: 0.75,
  },
  {
    id: "contact",
    header: "Primary Contact",
    accessorKey: "contact",
    size: 175,
    flex: 1.05,
  },
  { id: "phone", header: "Phone", accessorKey: "phone", size: 155, flex: 0.95 },
  {
    id: "actions",
    header: "Actions",
    accessorFn: () => "",
    enableSorting: false,
    enableColumnFilter: false,
    enableExport: false,
    size: 82,
    flex: 0,
    cell: ({ row }) => (
      <ClientActionMenu
        client={row.original}
        onEdit={onEdit}
        onViewDetails={onViewDetails}
        onDelete={onDelete}
        canManage={canManage}
      />
    ),
  },
];

const formatClientDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-GB");
};

const ClientDetailsEmpty = ({ icon, title, message }) => (
  <div className="client-details__empty">
    <i className={`bx ${icon}`} aria-hidden="true" />
    <strong>{title}</strong>
    <span>{message}</span>
  </div>
);

const ClientDetailsView = ({ client, onClose }) => {
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (client) setActiveTab("overview");
  }, [client]);

  if (!client) return null;

  const initials = (client.name || "Client")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  const engagementCount = Number(client.engagements) || 0;
  const riskClass = (client.risk || "low").toLowerCase();
  const statusClass = (client.status || "inactive").toLowerCase();
  const tabs = [
    ["overview", "Overview"],
    ["engagements", "Engagements"],
    ["documents", "Documents"],
    ["reports", "Reports"],
    ["contacts", "Contacts"],
    ["history", "Notes & History"],
  ];

  const renderTab = () => {
    if (activeTab === "overview") {
      return (
        <>
          <section
            className="client-details__summaries"
            aria-label="Client summary"
          >
            <article className="client-details__summary-card">
              <i className="bx bx-briefcase-alt-2 is-blue" aria-hidden="true" />
              <div>
                <span>Engagement Summary</span>
                <strong>{engagementCount}</strong>
                <small>Total engagements</small>
              </div>
            </article>
            <article className="client-details__summary-card">
              <i className="bx bx-folder-open is-green" aria-hidden="true" />
              <div>
                <span>Document Summary</span>
                <strong>0</strong>
                <small>Total documents</small>
              </div>
            </article>
            <article className="client-details__summary-card">
              <i
                className="bx bx-bar-chart-alt-2 is-purple"
                aria-hidden="true"
              />
              <div>
                <span>Report Summary</span>
                <strong>0</strong>
                <small>Total reports</small>
              </div>
            </article>
            <article className="client-details__summary-card client-details__summary-card--risk">
              <i
                className="bx bx-shield-quarter is-orange"
                aria-hidden="true"
              />
              <div>
                <span>Client Risk Profile</span>
                <strong className={`is-${riskClass}`}>
                  {client.risk || "Low"}
                </strong>
                <small>Current risk level</small>
              </div>
            </article>
          </section>
          <section className="client-details__overview-grid">
            <article className="client-details__panel">
              <header>
                <h3>Engagements</h3>
                <button
                  type="button"
                  onClick={() => setActiveTab("engagements")}
                >
                  View all <i className="bx bx-right-arrow-alt" />
                </button>
              </header>
              {engagementCount ? (
                <div className="client-details__record">
                  <i className="bx bx-briefcase" />
                  <div>
                    <strong>
                      {engagementCount} linked engagement
                      {engagementCount === 1 ? "" : "s"}
                    </strong>
                    <span>Open the Engagements tab to review them.</span>
                  </div>
                </div>
              ) : (
                <ClientDetailsEmpty
                  icon="bx-briefcase"
                  title="No engagements yet"
                  message="Engagements linked to this client will appear here."
                />
              )}
            </article>
            <article className="client-details__panel">
              <header>
                <h3>Documents</h3>
                <button type="button" onClick={() => setActiveTab("documents")}>
                  View all <i className="bx bx-right-arrow-alt" />
                </button>
              </header>
              <ClientDetailsEmpty
                icon="bx-folder-open"
                title="No documents yet"
                message="Client documents will appear here when available."
              />
            </article>
            <article className="client-details__panel">
              <header>
                <h3>Reports</h3>
                <button type="button" onClick={() => setActiveTab("reports")}>
                  View all <i className="bx bx-right-arrow-alt" />
                </button>
              </header>
              <ClientDetailsEmpty
                icon="bx-bar-chart-alt-2"
                title="No reports yet"
                message="Generated client reports will appear here."
              />
            </article>
          </section>
          <article className="client-details__panel client-details__notes-panel">
            <header>
              <h3>Client Notes &amp; History</h3>
              <button type="button" onClick={() => setActiveTab("history")}>
                View all history <i className="bx bx-right-arrow-alt" />
              </button>
            </header>
            <div className="client-details__history-row">
              <i className="bx bx-time-five" aria-hidden="true" />
              <div>
                <strong>Client record last updated</strong>
                <span>{formatClientDate(client.updatedOn)}</span>
              </div>
            </div>
          </article>
        </>
      );
    }

    if (activeTab === "contacts") {
      return (
        <section className="client-details__tab-panel">
          <header className="client-details__tab-panel-header">
            <div>
              <h3>Contacts</h3>
              <p>All contacts associated with this client.</p>
            </div>
          </header>
          <div className="client-details__table-wrap">
            <table className="client-details__table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role / Title</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Contact Type</th>
                  <th>Primary</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <span className="client-details__contact-avatar">
                      {initials}
                    </span>
                    {client.contact || "Not provided"}
                  </td>
                  <td>Primary contact</td>
                  <td>{client.email || "Not provided"}</td>
                  <td>{client.phone || "Not provided"}</td>
                  <td>Primary</td>
                  <td>
                    <i
                      className="bx bxs-star client-details__primary-star"
                      aria-label="Primary contact"
                    />
                  </td>
                  <td>
                    <span
                      className={`client-status client-status--${statusClass}`}
                    >
                      {client.status || "Inactive"}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      );
    }

    if (activeTab === "history") {
      return (
        <section className="client-details__tab-panel">
          <header className="client-details__tab-panel-header">
            <div>
              <h3>Notes &amp; History</h3>
              <p>Client notes and recent updates.</p>
            </div>
          </header>
          <div className="client-details__notes-content">
            <h4>Notes</h4>
            <p>
              {client.notes || "No notes have been recorded for this client."}
            </p>
            <div className="client-details__history-row">
              <i className="bx bx-edit-alt" />
              <div>
                <strong>Client details updated</strong>
                <span>{formatClientDate(client.updatedOn)}</span>
              </div>
            </div>
          </div>
        </section>
      );
    }

    const labels = {
      engagements: ["Engagements", "bx-briefcase"],
      documents: ["Documents", "bx-folder-open"],
      reports: ["Reports", "bx-bar-chart-alt-2"],
    };
    const [label, icon] = labels[activeTab];
    return (
      <section className="client-details__tab-panel">
        <header className="client-details__tab-panel-header">
          <div>
            <h3>{label}</h3>
            <p>Records linked to this client.</p>
          </div>
        </header>
        <ClientDetailsEmpty
          icon={icon}
          title={`No ${label.toLowerCase()} yet`}
          message={`${label} linked to this client will appear here.`}
        />
      </section>
    );
  };

  return (
    <div className="client-details">
      <main className="client-details__body piers-page">
        <nav className="client-details__breadcrumb" aria-label="Breadcrumb">
          <ol>
            <li>
              <button type="button" onClick={onClose}>
                Clients
              </button>
            </li>
            <li aria-hidden="true">
              <i className="bx bx-chevron-right" />
            </li>
            <li>
              <span aria-current="page">Client Details</span>
            </li>
          </ol>
        </nav>
        <header className="client-details__page-title piers-page-header">
          <h1 className="piers-page-title">Client Details</h1>
          <button
            type="button"
            className="client-details__close"
            onClick={onClose}
            aria-label="Back to clients"
          >
            <i className="bx bx-x" />
          </button>
        </header>
        <section className="client-details__hero">
          <div className="client-details__identity">
            <div className="client-details__avatar">{initials}</div>
            <div>
              <div className="client-details__title">
                <h2>{client.name || "Client"}</h2>
                <span className={`client-status client-status--${statusClass}`}>
                  {client.status || "Inactive"}
                </span>
              </div>
              <div className="client-details__meta">
                <span>
                  <i className="bx bx-receipt" /> {client.code || "—"}
                </span>
                <span>
                  <i className="bx bx-user" /> {client.type || "Not specified"}
                </span>
                <span>
                  <i className="bx bx-purchase-tag" />{" "}
                  {client.industry || "Not specified"}
                </span>
              </div>
              <div className="client-details__contact-info">
                <div>
                  <span>Primary Contact</span>
                  <strong>{client.contact || "Not provided"}</strong>
                </div>
                <div>
                  <span>Email</span>
                  <strong>{client.email || "Not provided"}</strong>
                </div>
                <div>
                  <span>Phone</span>
                  <strong>{client.phone || "Not provided"}</strong>
                </div>
              </div>
            </div>
          </div>
          <aside className="client-details__facts">
            <div>
              <span>Risk Level</span>
              <b className={`client-pill client-pill--${riskClass}`}>
                {client.risk || "Low"}
              </b>
            </div>
            <div>
              <span>Status</span>
              <b className={`client-status client-status--${statusClass}`}>
                {client.status || "Inactive"}
              </b>
            </div>
            <div>
              <span>Client Since</span>
              <strong>
                {formatClientDate(client.createdOn || client.updatedOn)}
              </strong>
            </div>
            <div>
              <span>Last Updated</span>
              <strong>{formatClientDate(client.updatedOn)}</strong>
            </div>
          </aside>
        </section>
        <nav
          className="client-details__tabs"
          aria-label="Client detail sections"
        >
          {tabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={activeTab === key ? "is-active" : ""}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="client-details__tab-content">{renderTab()}</div>
      </main>
    </div>
  );
};

export const ClientDetailsPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setClient(null);
    const id = Number(clientId);
    if (!Number.isInteger(id) || id <= 0) {
      setError("This client is unavailable.");
      setLoading(false);
      return () => {
        active = false;
      };
    }
    getClientDetail(id)
      .then((response) => {
        if (!active) return;
        if (!response?.status || !response.data)
          throw new Error(
            response?.message || "Unable to load client details."
          );
        setClient(response.data);
      })
      .catch((reason) => {
        if (active)
          setError(reason.message || "Unable to load client details.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientId, revision]);

  return (
    <div className="page-content client-details-page piers-page">
      {loading ? (
        <div className="client-details-page__message" role="status">
          Loading client details…
        </div>
      ) : error ? (
        <div className="client-details-page__message piers-page-header" role="alert">
          <h1 className="piers-page-title">Client Details</h1>
          <p>{error}</p>
          <button type="button" onClick={() => navigate("/clients/list")}>
            Back to Clients
          </button>
          <button
            type="button"
            onClick={() => setRevision((value) => value + 1)}
          >
            Retry
          </button>
        </div>
      ) : (
        <ClientDetailsView
          client={client}
          onClose={() => navigate("/clients/list")}
        />
      )}
    </div>
  );
};

const ClientList = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [gridLoading, setGridLoading] = useState(true);
  const [gridError, setGridError] = useState("");
  const [clientTypes, setClientTypes] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newClientDrawerOpen, setNewClientDrawerOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientPendingDeletion, setClientPendingDeletion] = useState(null);
  const [deleteFailure, setDeleteFailure] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState(blankClient);
  const [initialForm, setInitialForm] = useState(blankClient);
  const [errors, setErrors] = useState({});
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [clientImportOpen, setClientImportOpen] = useState(false);
  const [isEngagementMember, setIsEngagementMember] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    status: "All",
    type: "All",
    industry: "All",
    risk: "All",
    engagements: "All",
  });

  const filterOptions = useMemo(
    () => ({
      status: ["All", "Active", "Inactive"],
      type: ["All", ...clientTypes.map((item) => item.name)],
      industry: ["All", ...industryTypes.map((item) => item.name)],
      risk: ["All", "High", "Medium", "Low"],
    }),
    [clientTypes, industryTypes]
  );

  const loadClients = async () => {
    setGridLoading(true);
    setGridError("");
    try {
      const response = await searchClients();
      if (!response.status)
        throw new Error(response.message || "Unable to load clients.");
      setClients(response.data || []);
      window.dispatchEvent(new Event("piers:readiness-changed"));
    } catch (error) {
      setGridError(error.message || "Unable to load clients.");
    } finally {
      setGridLoading(false);
    }
  };

  const closeClientImport = () => setClientImportOpen(false);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [clientsResponse, typesResponse, industriesResponse, account] =
          await Promise.all([
            searchClients(),
            getClientTypes(),
            getIndustryTypes(),
            getLoggedInUser(),
          ]);
        const roleName =
          account?.role?.name ||
          account?.roleName ||
          account?.userRole?.name ||
          account?.designation ||
          "";
        setIsEngagementMember(
          Number(account?.roleId) === 4 || /engagement\s+member/i.test(roleName)
        );
        if (clientsResponse.status) setClients(clientsResponse.data || []);
        else setGridError(clientsResponse.message || "Unable to load clients.");
        if (typesResponse.status) setClientTypes(typesResponse.data || []);
        else
          ErrorNotify(typesResponse.message || "Unable to load client types.");
        if (industriesResponse.status)
          setIndustryTypes(industriesResponse.data || []);
        else
          ErrorNotify(
            industriesResponse.message || "Unable to load industries."
          );
      } catch {
        setGridError("Unable to load client data.");
      } finally {
        setGridLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const visibleClients = useMemo(
    () =>
      clients.filter((client) => {
        const term = search.toLowerCase();
        return (
          (!term ||
            [client.name, client.code, client.contact, client.email].some(
              (value) => (value || "").toLowerCase().includes(term)
            )) &&
          (filters.status === "All" || client.status === filters.status) &&
          (filters.type === "All" || client.type === filters.type) &&
          (filters.industry === "All" ||
            client.industry === filters.industry) &&
          (filters.risk === "All" || client.risk === filters.risk) &&
          (filters.engagements === "All" ||
            (filters.engagements === "Has engagements" &&
              client.engagements > 0) ||
            (filters.engagements === "No engagements" &&
              client.engagements === 0))
        );
      }),
    [clients, search, filters]
  );

  const updateFilter = (event) =>
    setFilters((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));

  const resetFilters = () => {
    setSearch("");
    setFilters({
      status: "All",
      type: "All",
      industry: "All",
      risk: "All",
      engagements: "All",
    });
  };

  const openDrawer = async (client = null) => {
    setEditingClient(client);
    if (client) {
      const matchingType = clientTypes.find(
        (item) => item.id === client.clientTypeId || item.name === client.type
      );
      const matchingIndustry = industryTypes.find(
        (item) =>
          item.id === client.industryTypeId || item.name === client.industry
      );
      const clientForm = {
        name: client.name || "",
        code: client.code || "",
        type: String(client.clientTypeId || matchingType?.id || ""),
        industry: String(client.industryTypeId || matchingIndustry?.id || ""),
        contact: client.contact || client.primaryContactName || "",
        email: client.email || "",
        phone: client.phone || "",
        risk: client.risk || client.riskLevel || "",
        status:
          client.status || (client.isActive === false ? "Inactive" : "Active"),
        notes: client.notes || "",
      };
      setForm(clientForm);
      setInitialForm(clientForm);
      setDrawerOpen(true);
      return;
    }
    setForm(blankClient);
    setInitialForm(blankClient);
    setDrawerOpen(true);
    setIsGeneratingCode(true);
    try {
      const response = await getNextClientCode();
      if (response.status) {
        const newClientForm = { ...blankClient, code: response.data || "" };
        setForm(newClientForm);
        setInitialForm(newClientForm);
        return;
      }
      ErrorNotify(response.message || "Unable to generate a client code.");
    } catch {
      ErrorNotify("Unable to generate a client code.");
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingClient(null);
    setForm(blankClient);
    setInitialForm(blankClient);
    setErrors({});
  };

  const openClientDetails = (client) => navigate(`/clients/${client.id}`);

  const requestDeleteClient = (client) => {
    setClientPendingDeletion(client);
    setDeleteFailure("");
  };

  const closeDeleteClient = () => {
    if (isDeleting) return;
    setClientPendingDeletion(null);
    setDeleteFailure("");
  };

  const confirmDeleteClient = async () => {
    if (!clientPendingDeletion || isDeleting) return;
    setIsDeleting(true);
    setDeleteFailure("");
    try {
      const response = await deleteClient(clientPendingDeletion.id);
      if (!response.status) {
        setDeleteFailure(
          response.message ||
            "This client cannot be deleted because it is associated with existing records."
        );
        return;
      }
      const deletedClientId = clientPendingDeletion.id;
      const deletedClientName = clientPendingDeletion.name;
      setClientPendingDeletion(null);
      setClients((current) =>
        current.filter((client) => client.id !== deletedClientId)
      );
      window.dispatchEvent(new Event("piers:readiness-changed"));
      SuccessNotify(
        response.message || `${deletedClientName} deleted successfully.`
      );
    } catch {
      setDeleteFailure(
        "PIERS could not verify whether this client can be deleted. Please try again."
      );
    } finally {
      setIsDeleting(false);
    }
  };
  const isFormDirty = Object.keys(blankClient).some(
    (field) => form[field] !== initialForm[field]
  );

  const requestCloseDrawer = () => {
    if (isFormDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    closeDrawer();
  };

  const discardDraft = () => {
    setDiscardConfirmOpen(false);
    closeDrawer();
  };

  const updateForm = (event) =>
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));

  const createClient = async (event) => {
    event.preventDefault();
    if (isSaving) return;
    const nextErrors = {};
    ["name", "code", "type", "contact", "email"].forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "Required";
    });
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setIsSaving(true);
    try {
      const response = await createUpdateClient({
        id: editingClient?.id || 0,
        clientName: form.name,
        clientCode: form.code,
        clientTypeId: Number(form.type),
        industryTypeId: form.industry ? Number(form.industry) : null,
        primaryContactName: form.contact,
        email: form.email,
        phone: form.phone || null,
        riskLevel: form.risk || "Low",
        isActive: form.status === "Active",
        notes: form.notes || null,
      });
      if (!response.status) {
        ErrorNotify(response.message || "Unable to create client.");
        return;
      }
      await loadClients();
      SuccessNotify(
        response.message ||
          `Client ${editingClient ? "updated" : "created"} successfully.`
      );
      closeDrawer();
    } catch {
      ErrorNotify("Unable to create client.");
    } finally {
      setIsSaving(false);
    }
    /* Legacy mock creation removed:
    setClients((current) => [
      {
        id: current.length + 1,
        name: form.name,
        code: form.code.toUpperCase(),
        type: form.type,
        industry: form.industry || "Not specified",
        risk: form.risk || "Low",
        status: form.status,
        engagements: 0,
        contact: form.contact,
        phone: form.phone || "—",
        updated: "Today",
      },
      ...current,
    ]);
    closeDrawer(); */
  };

  /* Legacy mock statistics removed:
  const legacyStatCards = [
    [
      "bx-group",
      "blue",
      "Total Clients",
      clients.length + 118,
      "↑ 8 vs last month",
    ],
    [
      "bx-buildings",
      "green",
      "Active Clients",
      clients.filter((item) => item.status === "Active").length + 103,
      "88% of total",
    ],
    ["bx-receipt", "orange", "Total Engagements", 245, "↑ 12 vs last month"],
    ["bx-flag", "purple", "High Risk Clients", 18, "14% of total"],
    ["bx-calendar-event", "red", "Due for Review", 27, "↑ 5 vs last month"],
  ]; */

  const totalEngagements = clients.reduce(
    (total, client) => total + (Number(client.engagements) || 0),
    0
  );
  const activeClients = clients.filter(
    (client) => client.status === "Active"
  ).length;
  const highRiskClients = clients.filter(
    (client) => client.risk === "High"
  ).length;
  const statCards = [
    ["bx-group", "blue", "Total Clients", clients.length, "All client records"],
    [
      "bx-buildings",
      "green",
      "Active Clients",
      activeClients,
      clients.length
        ? `${Math.round((activeClients / clients.length) * 100)}% of total`
        : "0% of total",
    ],
    [
      "bx-receipt",
      "orange",
      "Total Engagements",
      totalEngagements,
      "Linked client engagements",
    ],
    [
      "bx-flag",
      "purple",
      "High Risk Clients",
      highRiskClients,
      clients.length
        ? `${Math.round((highRiskClients / clients.length) * 100)}% of total`
        : "0% of total",
    ],
  ];

  return (
    <div className="page-content clients-page piers-page">
      <header className="clients-page__header piers-page-header">
        <div>
          <h2 className="piers-page-title">Clients</h2>
          <p>Manage and maintain all clients and organizations.</p>
        </div>
        <div className="clients-page__actions">
          <Button
            className="clients-refresh-button piers-refresh-button"
            onClick={loadClients}
            title="Refresh clients"
          >
            <i className="bx bx-refresh" /> Refresh
          </Button>
          {!isEngagementMember && (
            <>
              <Button
                className="clients-import-button"
                onClick={() => setClientImportOpen(true)}
                title="Import clients"
              >
                <i className="bx bx-upload" /> Import Clients
              </Button>
              <Button
                className="clients-new-button"
                data-onboarding-target="clients"
                onClick={() => setNewClientDrawerOpen(true)}
              >
                <i className="bx bx-plus" /> New Client
              </Button>
            </>
          )}
        </div>
      </header>

      <SummaryCards items={statCards} ariaLabel="Client summary" />

      

      <section className="clients-grid-card">
        <CustomDataGrid
 filterContent={<section className="clients-filter-panel" aria-label="Client filters">
        <div className="clients-filter-panel__fields">
          <FilterSelect
            label="Status"
            name="status"
            options={filterOptions.status}
            value={filters.status}
            onChange={updateFilter}
          />
          <FilterSelect
            label="Client Type"
            name="type"
            options={filterOptions.type}
            value={filters.type}
            onChange={updateFilter}
          />
          <FilterSelect
            label="Industry"
            name="industry"
            options={filterOptions.industry}
            value={filters.industry}
            onChange={updateFilter}
          />
          <FilterSelect
            label="Risk Level"
            name="risk"
            options={filterOptions.risk}
            value={filters.risk}
            onChange={updateFilter}
          />
          <FilterSelect
            label="Engagements"
            name="engagements"
            options={["All", "Has engagements", "No engagements"]}
            value={filters.engagements}
            onChange={updateFilter}
          />
        </div>
        <div className="clients-filter-panel__actions">
          <button
            className="clients-reset"
            type="button"
            onClick={resetFilters}
          >Clear</button>
        </div>
      </section>}

          data={visibleClients}
          loading={gridLoading}
          emptyState={{
            title: "No clients yet",
            icon: "bx-buildings",
            error: gridError,
            onRetry: loadClients,
            description: isEngagementMember
              ? "Clients available to your account appear here. Contact your firm administrator if you need access."
              : "Clients are the organizations whose engagements are managed and reviewed through PIERS.",
            filtered:
              clients.length > 0 ||
              Boolean(search) ||
              Object.values(filters).some((value) => value !== "All"),
            onReset: resetFilters,
            onAction: !isEngagementMember
              ? () => setNewClientDrawerOpen(true)
              : undefined,
            actionLabel: "Add Your First Client",
            onSecondary: !isEngagementMember
              ? () => setClientImportOpen(true)
              : undefined,
            secondaryLabel: "Import Clients",
            helpHref: "/piers/getting-started",
          }}
          columns={getClientColumns(
            openDrawer,
            openClientDetails,
            requestDeleteClient,
            !isEngagementMember
          )}
          fileName="clients"
          recordLabel="clients"
          initialPageSize={10}
          showToolbar={false}
          rowHeight={56}
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e4e8f0",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".2px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              borderBottomColor: "#edf0f5",
              color: "#273957",
              fontSize: 13,
            },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#f8faff" },
            "& .MuiDataGrid-footerContainer": {
              borderTopColor: "#e7ebf2",
              fontSize: 14,
            },
            "& .MuiDataGrid-footerContainer .MuiTypography-root": {
              fontSize: 14,
            },
          }}
        />
        {false && (
          <>
            <div className="clients-table-wrap">
              <table className="clients-table">
                <thead>
                  <tr>
                    <th>
                      <input aria-label="Select all clients" type="checkbox" />
                    </th>
                    <th>
                      Client Name <i className="bx bx-sort-alt-2" />
                    </th>
                    <th>
                      Client Code <i className="bx bx-sort-alt-2" />
                    </th>
                    <th>Client Type</th>
                    <th>Industry</th>
                    <th>Risk Level</th>
                    <th>Status</th>
                    <th>Engagements</th>
                    <th>Primary Contact</th>
                    <th>Phone</th>
                    <th>
                      Last Updated <i className="bx bx-sort-alt-2" />
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleClients.map((client) => (
                    <tr key={client.id}>
                      <td>
                        <input
                          aria-label={`Select ${client.name}`}
                          type="checkbox"
                        />
                      </td>
                      <td className="client-name">{client.name}</td>
                      <td>{client.code}</td>
                      <td>{client.type}</td>
                      <td>{client.industry}</td>
                      <td>
                        <span
                          className={`client-pill client-pill--${client.risk.toLowerCase()}`}
                        >
                          {client.risk}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`client-status client-status--${client.status.toLowerCase()}`}
                        >
                          {client.status}
                        </span>
                      </td>
                      <td>{client.engagements}</td>
                      <td>{client.contact}</td>
                      <td>{client.phone}</td>
                      <td>{client.updated}</td>
                      <td>
                        <button
                          className="client-action"
                          aria-label={`Actions for ${client.name}`}
                        >
                          <i className="bx bx-dots-vertical-rounded" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!visibleClients.length && (
                <div className="clients-empty">
                  No clients match the selected filters.
                </div>
              )}
            </div>
            <footer className="clients-pagination">
              <span>
                Showing 1 to {Math.min(10, visibleClients.length)} of{" "}
                {clients.length + 118} clients
              </span>
              <div>
                <label>
                  Rows per page{" "}
                  <select defaultValue="10">
                    <option>10</option>
                    <option>25</option>
                    <option>50</option>
                  </select>
                </label>
                <button>&laquo;</button>
                <button>‹</button>
                <button className="is-active">1</button>
                <button>2</button>
                <button>3</button>
                <button>…</button>
                <button>13</button>
                <button>&raquo;</button>
              </div>
            </footer>
          </>
        )}
      </section>

      <Offcanvas
        className="client-drawer"
        direction="end"
        isOpen={drawerOpen}
        toggle={requestCloseDrawer}
        backdrop="static"
      >
        <OffcanvasHeader toggle={requestCloseDrawer}>
          {editingClient ? "Edit Client" : "New Client"}
        </OffcanvasHeader>
        <OffcanvasBody>
          <p className="client-drawer__intro">
            {editingClient
              ? "Update the selected client record."
              : "Enter client information to create a new client record."}
          </p>
          <Form onSubmit={createClient} className="client-form">
            <h3>
              <i className="bx bx-buildings" aria-hidden="true" /> General
              Information
            </h3>
            <FormGroup>
              <Label>
                Client Name <b>*</b>
              </Label>
              <Input
                name="name"
                value={form.name}
                onChange={updateForm}
                placeholder="Enter client name"
                invalid={Boolean(errors.name)}
              />
              {errors.name && <small>{errors.name}</small>}
            </FormGroup>
            <FormGroup>
              <Label>
                Client Code <b>*</b>
              </Label>
              <Input
                name="code"
                value={form.code}
                onChange={updateForm}
                placeholder="Enter unique client code"
                invalid={Boolean(errors.code)}
              />
              {errors.code && <small>{errors.code}</small>}
              <small className="client-code-help">
                {isGeneratingCode
                  ? "Generating client code…"
                  : "Generated automatically. You can change it if needed."}
              </small>
            </FormGroup>
            <FormGroup>
              <Label>
                Client Type <b>*</b>
              </Label>
              <Input
                name="type"
                type="select"
                value={form.type}
                onChange={updateForm}
                invalid={Boolean(errors.type)}
              >
                <option value="">Select client type</option>
                {clientTypes.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Input>
              {errors.type && <small>{errors.type}</small>}
            </FormGroup>
            <FormGroup>
              <Label>Industry</Label>
              <Input
                name="industry"
                type="select"
                value={form.industry}
                onChange={updateForm}
              >
                <option value="">Select industry</option>
                {industryTypes.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <h3>
              <i className="bx bx-user" aria-hidden="true" /> Contact
              Information
            </h3>
            <FormGroup>
              <Label>
                Primary Contact Name <b>*</b>
              </Label>
              <Input
                name="contact"
                value={form.contact}
                onChange={updateForm}
                placeholder="Enter contact name"
                invalid={Boolean(errors.contact)}
              />
              {errors.contact && <small>{errors.contact}</small>}
            </FormGroup>
            <FormGroup>
              <Label>
                Email <b>*</b>
              </Label>
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={updateForm}
                placeholder="Enter email address"
                invalid={Boolean(errors.email)}
              />
              {errors.email && <small>{errors.email}</small>}
            </FormGroup>
            <FormGroup>
              <Label>Phone</Label>
              <Input
                name="phone"
                value={form.phone}
                onChange={updateForm}
                placeholder="Enter phone number"
              />
            </FormGroup>
            <h3>
              <i className="bx bx-detail" aria-hidden="true" /> Additional
              Information
            </h3>
            <FormGroup>
              <Label>Risk Level</Label>
              <Input
                name="risk"
                type="select"
                value={form.risk}
                onChange={updateForm}
              >
                <option value="">Select risk level</option>
                {filterOptions.risk.slice(1).map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input
                name="status"
                type="select"
                value={form.status}
                onChange={updateForm}
              >
                <option>Active</option>
                <option>Inactive</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Notes</Label>
              <Input
                name="notes"
                type="textarea"
                rows="4"
                value={form.notes}
                onChange={updateForm}
                placeholder="Enter any notes about this client..."
              />
            </FormGroup>
            <div className="client-drawer__footer">
              <Button
                type="button"
                outline
                onClick={requestCloseDrawer}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={isSaving}>
                {isSaving && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  />
                )}
                {isSaving
                  ? editingClient
                    ? "Saving changes…"
                    : "Creating client…"
                  : editingClient
                  ? "Save Changes"
                  : "Create Client"}
              </Button>
            </div>
          </Form>
        </OffcanvasBody>
      </Offcanvas>
      <NewClientDrawer
        clientTypes={clientTypes}
        industryTypes={industryTypes}
        isOpen={newClientDrawerOpen}
        onClose={() => setNewClientDrawerOpen(false)}
        onSaved={loadClients}
      />
      <UnsavedChangesDialog
        isOpen={discardConfirmOpen}
        onContinue={() => setDiscardConfirmOpen(false)}
        onDiscard={discardDraft}
        subject="client details"
      />
      <Modal
        className="client-delete-dialog"
        centered
        isOpen={Boolean(clientPendingDeletion)}
        toggle={closeDeleteClient}
        aria-labelledby="client-delete-title"
      >
        <ModalHeader toggle={closeDeleteClient}>
          <span id="client-delete-title">
            {deleteFailure ? "Client cannot be deleted" : "Delete client?"}
          </span>
        </ModalHeader>
        <ModalBody>
          {deleteFailure ? (
            <div className="client-delete-dialog__blocked" role="alert">
              <span>
                <i className="bx bx-error-circle" />
              </span>
              <div>
                <h3>Deletion blocked</h3>
                <p>{deleteFailure}</p>
              </div>
            </div>
          ) : (
            <div className="client-delete-dialog__confirmation">
              <span>
                <i className="bx bx-trash" />
              </span>
              <div>
                <p>
                  Are you sure you want to permanently delete{" "}
                  <strong>{clientPendingDeletion?.name}</strong>?
                </p>
                <small>
                  PIERS will check for linked engagements before deleting this
                  client.
                </small>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {deleteFailure ? (
            <Button color="primary" onClick={closeDeleteClient}>
              Close
            </Button>
          ) : (
            <>
              <Button
                outline
                color="secondary"
                onClick={closeDeleteClient}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                color="danger"
                onClick={confirmDeleteClient}
                disabled={isDeleting}
              >
                {isDeleting && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  />
                )}
                {isDeleting ? "Checking…" : "Delete client"}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={clientImportOpen}
        modalClassName="engagement-import-modal"
        onClosed={loadClients}
        toggle={closeClientImport}
      >
        <ModalBody className="p-0">
          {clientImportOpen && (
            <DataImportWizardPage
              closeAction={closeClientImport}
              initialImportType="client"
            />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ClientList;
